import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Users,
  Target,
  Mail,
  BarChart3,
  HeadphonesIcon,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Bell,
  Search
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Tenants', href: '/tenants', icon: Building2 },
  {
    name: 'Subscriptions',
    icon: CreditCard,
    children: [
      { name: 'Overview', href: '/subscriptions' },
      { name: 'Plans & Add-ons', href: '/subscriptions/plans' },
      { name: 'Invoices', href: '/subscriptions/invoices' }
    ]
  },
  {
    name: 'Sales',
    icon: Target,
    children: [
      { name: 'Leads', href: '/sales/leads' },
      { name: 'Opportunities', href: '/sales/opportunities' },
      { name: 'Pipeline', href: '/sales/pipeline' }
    ]
  },
  {
    name: 'Marketing',
    icon: Mail,
    children: [
      { name: 'Contacts', href: '/marketing/contacts' },
      { name: 'Lists', href: '/marketing/lists' },
      { name: 'Templates', href: '/marketing/templates' },
      { name: 'Campaigns', href: '/marketing/campaigns' }
    ]
  },
  {
    name: 'Analytics',
    icon: BarChart3,
    children: [
      { name: 'Overview', href: '/analytics' },
      { name: 'AI & Knox Learning', href: '/analytics/ai' }
    ]
  },
  { name: 'Support', href: '/support', icon: HeadphonesIcon },
  {
    name: 'Settings',
    icon: Settings,
    children: [
      { name: 'General', href: '/settings' },
      { name: 'Legal Entity', href: '/settings/legal-entity' },
      { name: 'Notifications', href: '/settings/notifications' },
      { name: 'Admin Users', href: '/settings/users' },
      { name: 'Terms & Conditions', href: '/settings/terms' },
      { name: 'Audit Logs', href: '/settings/audit-logs' },
      // TEMPORARY one-time key export (remove after use)
      { name: 'Key Export', href: '/settings/key-export' }
    ]
  }
];

const NavItem = ({ item, isActive, onClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const hasChildren = item.children && item.children.length > 0;

  const isChildActive = hasChildren && item.children.some(
    child => location.pathname === child.href || location.pathname.startsWith(child.href + '/')
  );

  const activeClass = isActive || isChildActive
    ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-600'
    : 'text-gray-700 hover:bg-gray-50 border-l-4 border-transparent';

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium ${activeClass}`}
        >
          <div className="flex items-center">
            <item.icon className="w-5 h-5 mr-3" />
            {item.name}
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="ml-8 mt-1 space-y-1">
            {item.children.map((child) => (
              <Link
                key={child.href}
                to={child.href}
                onClick={onClick}
                className={`block px-4 py-2 text-sm ${
                  location.pathname === child.href
                    ? 'text-primary-700 font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {child.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={`flex items-center px-4 py-2.5 text-sm font-medium ${activeClass}`}
    >
      <item.icon className="w-5 h-5 mr-3" />
      {item.name}
    </Link>
  );
};

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <Link to="/" className="flex items-center">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="ml-2 text-lg font-semibold text-gray-900">Admin Portal</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="space-y-1">
            {navigation.map((item) => (
              <NavItem
                key={item.name}
                item={item}
                isActive={location.pathname === item.href}
                onClick={() => setSidebarOpen(false)}
              />
            ))}
          </div>
        </nav>

        {/* User menu */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-700 font-medium">
                {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="ml-2 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1 flex items-center justify-end space-x-4">
              {/* Search */}
              <div className="hidden sm:block relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Notifications */}
              <button className="relative p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <Bell className="w-6 h-6" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
